import React, { useState, useEffect } from "react";
import {
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  UserMinusIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  BuildingOffice2Icon,
} from "@heroicons/react/24/outline";
import MetricCard from "../components/MetricCard";
import FilterBar from "../components/FilterBar";
import ChannelDistribution from "../components/ChannelDistribution";
import HeatMap from "../components/HeatMap";
import SLAAlerts from "../components/SLAAlerts";
import AiVsHuman from "../components/AiVsHuman";
import { metricsService, exportUrls } from "../services/api";
import { formatDateTime } from "../utils/datetime";

// Função para formatar minutos em hh:mm
const formatMinutesToHHMM = (minutes) => {
  if (!minutes || isNaN(minutes)) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // A conta é obrigatória: sem ela o dashboard somaria operações distintas
  const hasAccount = (filters.accountIds || []).length > 0;
  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    if (!hasAccount) {
      setDashboardData(null);
      setError(null);
      setLoading(false);
      return;
    }

    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await metricsService.getDashboard(filters);
        setDashboardData(response.data.data);
        setLastUpdate(new Date());
      } catch (err) {
        console.error("Error loading dashboard:", err);
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, hasAccount]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExportAgentConversations = (agentId) => {
    window.open(exportUrls.agentConversations(agentId, filters), '_blank');
  };

  const handleExportAgentMetrics = () => {
    window.open(exportUrls.agentMetrics(filters), '_blank');
  };

  const serviceLevel = dashboardData?.serviceLevel || {};
  const tma = dashboardData?.tma || {};
  const tme = dashboardData?.tme || {};
  const totals = dashboardData?.conversationTotals || {};
  const agentMetrics = dashboardData?.agentMetrics || [];
  const channelDistribution = dashboardData?.channelDistribution || [];

  // Contas presentes no recorte atual — define se a visão é comparativa
  const accountsInView = [
    ...new Set(channelDistribution.map((item) => item.account_name).filter(Boolean)),
  ];
  const multiAccount = accountsInView.length > 1;

  // Ranking de canais: agrupado por conta quando há mais de uma no recorte
  const rankingGroups = multiAccount
    ? accountsInView.map((account) => ({
        account,
        items: channelDistribution
          .filter((item) => item.account_name === account)
          .sort((a, b) => b.total_conversations - a.total_conversations),
      }))
    : [{ account: null, items: [...channelDistribution].sort((a, b) => b.total_conversations - a.total_conversations) }];

  // Sem conta selecionada o dashboard não busca nada: somar operações
  // diferentes (Lets, Suporte N1, Suporte N2) produz um número que não
  // representa nenhuma delas.
  if (!hasAccount) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <FilterBar onFilterChange={handleFilterChange} />

        <div className="flex flex-col items-center justify-center rounded-lg bg-white p-12 text-center shadow">
          <div className="mb-4 rounded-full bg-blue-50 p-4">
            <BuildingOffice2Icon className="h-10 w-10 text-blue-600" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Selecione uma conta para começar</h2>
          <p className="max-w-md text-gray-500">
            A plataforma atende múltiplas operações. Escolha uma ou mais contas no filtro acima —
            é possível combiná-las para comparar resultados.
          </p>
        </div>
      </div>
    );
  }

  if (loading && !dashboardData) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <FilterBar onFilterChange={handleFilterChange} />
        <div className="flex items-center justify-center rounded-lg bg-white p-12 shadow">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <FilterBar onFilterChange={handleFilterChange} />
        <div className="rounded-lg border border-red-200 bg-red-50 p-6">
          <h3 className="mb-2 font-semibold text-red-800">Erro ao carregar dashboard</h3>
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setFilters({ ...filters })}
            className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Filtros */}
      <FilterBar onFilterChange={handleFilterChange} />

      {loading && (
        <div className="mb-4 flex items-center gap-2 rounded-md bg-blue-50 px-4 py-2 text-sm text-blue-700">
          <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-blue-600"></div>
          Atualizando métricas...
        </div>
      )}

      {/* Cards de Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        <MetricCard
          title="Nível de Serviço"
          value={`${serviceLevel.service_level_percentage || 0}%`}
          subtitle={`${serviceLevel.conversations_within_sla || 0} de ${
            serviceLevel.total_conversations || 0
          } conversas`}
          icon={<ChartBarIcon className="w-6 h-6" />}
          color="blue"
          tooltip={{
            title: "Nível de Serviço (SLA) - Prazo: 24 horas",
            description: "Percentual de conversas atendidas dentro do prazo estabelecido pelo SLA (Service Level Agreement). O prazo de referência aplicado é de 24 horas para a primeira resposta ao cliente. Mede a eficiência do time em responder dentro dos prazos acordados.",
            formula: "(Conversas dentro do SLA / Total de conversas) × 100",
            example: "Se 85 de 100 conversas receberam primeira resposta em até 24 horas: (85/100) × 100 = 85%"
          }}
        />

        <MetricCard
          title="TMA - Tempo Médio de Atendimento"
          value={formatMinutesToHHMM(tma.tma_minutes)}
          subtitle={`${tma.total_resolved || 0} conversas resolvidas`}
          icon={<ClockIcon className="w-6 h-6" />}
          color="green"
          tooltip={{
            title: "Tempo Médio de Atendimento (TMA)",
            description: "Tempo médio desde a primeira resposta do agente até a resolução da conversa. Indica a eficiência na resolução de problemas dos clientes.",
            formula: "Soma do tempo de atendimento / Total de conversas resolvidas",
            example: "Se 10 conversas foram resolvidas em 300 minutos total: 300/10 = 30 minutos (00:30)"
          }}
        />

        <MetricCard
          title="TME - Tempo Médio de Espera"
          value={formatMinutesToHHMM(tme.tme_minutes)}
          subtitle={`${tme.total_conversations || 0} conversas`}
          icon={<UserGroupIcon className="w-6 h-6" />}
          color="yellow"
          tooltip={{
            title: "Tempo Médio de Espera (TME)",
            description: "Tempo médio que o cliente aguarda desde o início da conversa até receber a primeira resposta do agente. Métrica importante para avaliar a velocidade do primeiro contato.",
            formula: "Soma do tempo de espera / Total de conversas",
            example: "Se clientes aguardaram 150 minutos no total em 10 conversas: 150/10 = 15 minutos (00:15)"
          }}
        />
      </div>

      {/* Volume atendido pela IA x humano */}
      <AiVsHuman data={dashboardData?.aiVsHuman} />

      {/* Cards de Totais de Conversas */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-700 mb-3">Visão Geral de Conversas</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <MetricCard
            title="Total"
            value={totals.total_conversations || 0}
            subtitle="100%"
            icon={<ChatBubbleLeftRightIcon className="w-5 h-5" />}
            color="gray"
            tooltip={{
              title: "Total de Conversas",
              description: "Número total de conversas criadas no período selecionado, independente do status ou atribuição."
            }}
          />
          <MetricCard
            title="Abertas"
            value={totals.total_open || 0}
            subtitle={`${((totals.total_open / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<XCircleIcon className="w-5 h-5" />}
            color="orange"
            tooltip={{
              title: "Conversas Abertas",
              description: "Conversas que ainda não foram resolvidas (status: aberta, pendente ou snoozed)."
            }}
          />
          <MetricCard
            title="Fechadas"
            value={totals.total_closed || 0}
            subtitle={`${((totals.total_closed / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<CheckCircleIcon className="w-5 h-5" />}
            color="green"
            tooltip={{
              title: "Conversas Fechadas",
              description: "Conversas que foram resolvidas e fechadas."
            }}
          />
          <MetricCard
            title="Com Agente"
            value={totals.with_agent || 0}
            subtitle={`${((totals.with_agent / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<UserIcon className="w-5 h-5" />}
            color="blue"
            tooltip={{
              title: "Conversas com Agente",
              description: "Conversas que possuem um agente atribuído."
            }}
          />
          <MetricCard
            title="Sem Agente"
            value={totals.without_agent || 0}
            subtitle={`${((totals.without_agent / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<UserMinusIcon className="w-5 h-5" />}
            color="red"
            tooltip={{
              title: "Conversas sem Agente",
              description: "Conversas que ainda não foram atribuídas a nenhum agente."
            }}
          />
          <MetricCard
            title="Sem Resposta"
            value={totals.without_first_reply || 0}
            subtitle={`${((totals.without_first_reply / (totals.total_conversations || 1)) * 100).toFixed(1)}%`}
            icon={<ExclamationTriangleIcon className="w-5 h-5" />}
            color="red"
            tooltip={{
              title: "Conversas Sem Primeira Resposta",
              description: "Conversas que ainda não receberam a primeira resposta de um agente. Estas conversas NÃO entram no cálculo do Nível de Serviço acima."
            }}
          />
        </div>
      </div>

      {/* Gráficos e Visualizações */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <ChannelDistribution data={channelDistribution} />

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Ranking de Canais</h3>
          {channelDistribution.length > 0 ? (
            <div className="space-y-4 max-h-[26rem] overflow-y-auto">
              {rankingGroups.map((group) => (
                <div key={group.account || 'unico'}>
                  {group.account && (
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {group.account}
                    </p>
                  )}
                  <div className="space-y-3">
                    {group.items.map((channel, index) => (
                      <div
                        key={`${channel.account_id}-${channel.channel_name}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-bold">
                            {index + 1}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">
                              {channel.channel_name}
                            </p>
                            <p className="text-sm text-gray-500">
                              {channel.channel_type?.replace('Channel::', '')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900">
                            {channel.total_conversations}
                          </p>
                          <p className="text-sm text-gray-500">
                            {channel.percentage}%
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">Nenhum dado disponível</p>
          )}
        </div>
      </div>

      {/* Mapa de Calor */}
      <div className="mb-6">
        <HeatMap data={dashboardData?.heatMap || dashboardData?.heatmap} />
      </div>

      {/* Alertas de SLA */}
      <div className="mb-6">
        <SLAAlerts alerts={dashboardData?.slaAlerts?.items || []} />
      </div>

      {/* Tabela de Métricas por Agente */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Métricas por Agente</h3>
          <button
            onClick={handleExportAgentMetrics}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 text-sm"
            title="Exportar métricas de todos os agentes"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Exportar Métricas
          </button>
        </div>
        {agentMetrics.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conta / Times
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receptivas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ativas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Abertas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Resolvidas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pendentes
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adiadas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TME (min)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TMA (min)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-1">
                      <span>SLA %</span>
                      <div className="relative group">
                        <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        <div className="hidden group-hover:block absolute right-0 top-6 z-50 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg normal-case font-normal">
                          <div className="font-semibold mb-1">Nível de Serviço do Agente</div>
                          <div className="text-gray-300">
                            Entre as conversas do agente que <strong>receberam</strong> primeira resposta,
                            o percentual respondido em até 24 horas. Conversas nunca respondidas ficam de
                            fora da conta — mesma base do card "Nível de Serviço" no topo.
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-700 text-gray-300">
                            <div className="font-semibold text-white">Cores:</div>
                            <div className="mt-1 space-y-1">
                              <div>🟢 Verde: ≥ 80% (Excelente)</div>
                              <div>🟡 Amarelo: 60-79% (Atenção)</div>
                              <div>🔴 Vermelho: &lt; 60% (Crítico)</div>
                            </div>
                          </div>
                          <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                        </div>
                      </div>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {agentMetrics.map((agent) => {
                  // Mesma base do card de Nível de Serviço: apenas conversas
                  // que receberam primeira resposta entram no cálculo
                  const answered = Number(agent.conversations_with_first_reply) || 0;
                  const slaPercentage = answered
                    ? ((agent.conversations_within_sla / answered) * 100).toFixed(1)
                    : null;
                  return (
                    <tr key={`${agent.agent_id}-${agent.account_id}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.agent_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {agent.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 min-w-[10rem]">
                        <div className="whitespace-nowrap text-sm text-gray-900">
                          {agent.account_name || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {agent.team_names || 'Sem time'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.total_conversations}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-indigo-100 text-indigo-800">
                          {agent.receptive_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-purple-100 text-purple-800">
                          {agent.active_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800">
                          {agent.open_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800">
                          {agent.resolved_conversations}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-yellow-100 text-yellow-800">
                          {agent.pending_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="px-2 py-1 text-xs font-semibold rounded bg-orange-100 text-orange-800">
                          {agent.snoozed_conversations || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatMinutesToHHMM(agent.avg_first_response_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatMinutesToHHMM(agent.avg_resolution_time)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {slaPercentage === null ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-600">
                            sem resposta
                          </span>
                        ) : (
                          <>
                            <span
                              className={`px-2 py-1 text-xs font-semibold rounded ${
                                slaPercentage >= 80
                                  ? "bg-green-100 text-green-800"
                                  : slaPercentage >= 60
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {slaPercentage}%
                            </span>
                            <div className="mt-1 whitespace-nowrap text-xs text-gray-500">
                              {agent.conversations_within_sla}/{answered} resp.
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleExportAgentConversations(agent.agent_id)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title={`Exportar conversas de ${agent.agent_name}`}
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500">Nenhum dado disponível</p>
        )}
      </div>

      {/* Rodapé */}
      <div className="mt-6 text-center text-sm text-gray-500">
        {accountsInView.length > 0 && (
          <span className="mr-2 font-medium text-gray-600">
            {accountsInView.join(' · ')} |
          </span>
        )}
        Última atualização: {formatDateTime(lastUpdate)}
      </div>
    </div>
  );
};

export default Dashboard;
